import { EventEmitter, Injectable } from '@angular/core';
import { Subscription } from 'rxjs';

/**
 * Drives an explicit, data-loading spinner. Components subscribe via `onPageEvent()` and render
 * their own loader, so loading feedback belongs to the view doing the work.
 *
 * Intentionally does NOT hook router events: route transitions don't show a global spinner. The
 * destination view owns its loader and lazy modules are preloaded (see SelectivePreloadStrategy),
 * so switching tabs is immediate and no spinner ever appears over the outgoing view. A previous
 * implementation listened to NavigationStart with a 1s timer; that surfaced in the still-mounted
 * Details (dryer-table) view whenever a lazy chunk took >1s, and the router subscription was never
 * torn down (one leak per component that called onInit()).
 */
@Injectable({
  providedIn: 'root',
})
export class PageLoaderService {
  private readonly pageEvent$ = new EventEmitter<boolean>();

  onPageEvent(next: (value: boolean) => void): Subscription {
    return this.pageEvent$.subscribe(next);
  }

  showSpinnerForDataLoading(subscription: Subscription, delayInMilliseconds = 0): void {
    this.pageEvent$.emit(true);
    subscription.add(() => {
      setTimeout(() => {
        this.pageEvent$.emit(false);
      }, delayInMilliseconds);
    });
  }

  forceCloseSpinner(): void {
    this.pageEvent$.emit(false);
  }
}
